import React, { useState, useEffect, useMemo, useId, useCallback } from 'react';
import Chart from 'react-apexcharts';
import { Skeleton } from '../ui/Skeleton';
import { computeTrailingWindowAverage } from '../../utils/timeSeriesSmoothing';
import { getDarkLineAreaChartBase, mergeChartOptions } from '../../utils/darkChartTheme';
import {
  buildCategoryZoomChartEvents,
  sliceByCategoryZoom,
  type CategoryZoomRange,
} from './chartZoomUtils';
import {
  CHART_TOOLBAR_ROW_CLASS,
  CHART_TOOLBAR_SELECT_CLASS,
  CHART_TOOLBAR_TOGGLE_GROUP_CLASS,
  chartToolbarToggleBtnClass,
} from './chartToolbarTheme';

const CHART_TYPES = [
  { id: 'bar', label: 'Bars' },
  { id: 'area', label: 'Area' },
  { id: 'stack', label: 'Stack' },
];

const PERIODS = [
  { id: 'weekly', label: 'Week' },
  { id: 'monthly', label: 'Month' },
  { id: 'yearly', label: 'Year' },
];

const MA_WINDOWS = [
  { id: 'weekly', label: 'Week', days: 7 },
  { id: 'monthly', label: 'Month', days: 30 },
  { id: 'yearly', label: 'Year', days: 365 },
];

function formatLeadTime(minutes) {
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/**
 * Lead Time for Changes chart: deployment timestamp − commit timestamp per change, MLT = mean of those.
 * @param {string[]} dates - Deployment date strings (YYYY-MM-DD)
 * @param {number[]} leadTimeMinutes - Lead time in minutes per deployment (same length as dates)
 * @param {number} meanLeadTimeMinutes - Precomputed mean (for full window)
 * @param {string} period - 'weekly' | 'monthly' | 'yearly'
 * @param {string} chartType - 'area' | 'bar' | 'stack'
 * @param {(id: string) => void} onPeriodChange
 * @param {(id: string) => void} onChartTypeChange
 * @param {string} [title='Lead Time for Changes']
 * @param {number} [height=320]
 */
const LeadTimeChart = ({
  dates = [],
  leadTimeMinutes = [],
  meanLeadTimeMinutes,
  period,
  chartType,
  onPeriodChange,
  onChartTypeChange,
  title = 'Lead Time for Changes',
  height = 320,
  loading = false,
}: any) => {
  const chartUid = useId().replace(/:/g, '');
  const [zoomRange, setZoomRange] = useState<CategoryZoomRange>(null);
  const [useMovingAverage, setUseMovingAverage] = useState(true);
  const [maWindow, setMaWindow] = useState('weekly'); // weekly|monthly|yearly

  const n = dates.length;
  const maDays = (MA_WINDOWS.find((w) => w.id === maWindow) || MA_WINDOWS[0]).days;

  const seriesToRender = useMemo(() => {
    if (!useMovingAverage) return leadTimeMinutes;
    return computeTrailingWindowAverage({
      dates,
      values: leadTimeMinutes,
      windowDays: maDays,
      skipNulls: true,
    });
  }, [useMovingAverage, dates, leadTimeMinutes, maDays]);

  const isZoomed = zoomRange != null;

  const displayedValues = sliceByCategoryZoom<number>(seriesToRender, zoomRange);
  const displayedMean =
    displayedValues.length > 0
      ? displayedValues.reduce((a, b) => a + b, 0) / displayedValues.length
      : 0;
  const displayedCount = displayedValues.length;

  const rawMeanFromSeries = useMemo(() => {
    if (!leadTimeMinutes.length) return null;
    return leadTimeMinutes.reduce((a, b) => a + b, 0) / leadTimeMinutes.length;
  }, [leadTimeMinutes]);

  /** Prefer backend MLT when present (matches DORA service); else mean of plotted series. */
  const fullWindowMeanMinutes = useMemo(() => {
    if (meanLeadTimeMinutes != null && Number.isFinite(meanLeadTimeMinutes))
      return meanLeadTimeMinutes;
    return rawMeanFromSeries;
  }, [meanLeadTimeMinutes, rawMeanFromSeries]);

  /** Value for headline + horizontal rule: API window mean when full view + raw; else visible/MA mean. */
  const lineAndHeadlineMean = useMemo(() => {
    if (useMovingAverage) return displayedMean;
    if (isZoomed) return displayedMean;
    return fullWindowMeanMinutes ?? displayedMean;
  }, [useMovingAverage, isZoomed, displayedMean, fullWindowMeanMinutes]);

  const headlineLabel = useMovingAverage ? 'Moving avg' : isZoomed ? 'Mean (visible)' : 'Mean';

  useEffect(() => {
    setZoomRange(null);
  }, [dates, leadTimeMinutes, period]);

  const showXLabel = useCallback(
    (dataPointIndex) => {
      if (n <= 14) return true;
      if (n <= 35) return dataPointIndex % 5 === 0 || dataPointIndex === n - 1;
      if (period === 'yearly') {
        return (
          dataPointIndex === 0 || dataPointIndex === n - 1 || dataPointIndex === Math.floor(n / 2)
        );
      }
      const step = Math.max(1, Math.floor(n / 12));
      return dataPointIndex % step === 0 || dataPointIndex === n - 1;
    },
    [n, period]
  );

  const categories = useMemo(
    () =>
      dates.map((d) => {
        const [, m, day] = d.split('-');
        return `${String(Number(day)).padStart(2, '0')}/${String(Number(m)).padStart(2, '0')}`;
      }),
    [dates]
  );

  const handleZoomRangeChange = useCallback((range: CategoryZoomRange) => {
    setZoomRange(range);
  }, []);

  const chartOptions = useMemo(
    () =>
      mergeChartOptions(
        getDarkLineAreaChartBase({
          enableToolbar: true,
          chartType: chartType === 'bar' ? 'bar' : 'area',
        }),
        {
          chart: {
            id: `lead-time-${chartUid}`,
            animations: { enabled: true, easing: 'easeinout', speed: 400 },
            stacked: chartType === 'stack',
            ...buildCategoryZoomChartEvents(n, handleZoomRangeChange),
          },
          plotOptions: { bar: { borderRadius: 4, columnWidth: '70%', distributed: false } },
          dataLabels: { enabled: false },
          xaxis: {
            categories,
            ...(chartType === 'bar' && { tickPlacement: 'on' as const }),
            ...(period === 'yearly' && { tickAmount: 2 }),
            labels: {
              maxRotation: 45,
              hideOverlappingLabels: true,
              formatter: (value, _labelValue, opts) => (showXLabel(opts?.i ?? 0) ? value : ''),
            },
          },
          yaxis: {
            labels: { formatter: (v) => `${Number(v)} min` },
            tickAmount: 4,
            min: 0,
          },
          colors: ['#0D9488'],
          grid: {
            ...(chartType === 'bar' && { padding: { left: 10, right: 10 } }),
            xaxis: { lines: { show: false } },
          },
          tooltip: {
            x: {
              formatter: (_val, { dataPointIndex }) => {
                const d = dates[dataPointIndex];
                if (!d) return _val;
                const [, m, day] = d.split('-');
                return `${String(Number(m)).padStart(2, '0')}/${String(Number(day)).padStart(2, '0')}/${d.slice(0, 4)}`;
              },
            },
            y: { formatter: (v) => formatLeadTime(Number(v)) },
          },
        }
      ),
    [chartUid, chartType, categories, dates, handleZoomRangeChange, n, period, showXLabel]
  );

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div>
          <h4 className="text-base font-semibold text-slate-100">{title}</h4>
          <p className="text-2xl font-bold text-slate-100 mt-1">
            {loading ? (
              <span className="text-slate-400">Loading…</span>
            ) : (
              <>
                {useMovingAverage ? (
                  <>
                    {headlineLabel}:{' '}
                    <span className="text-brand-teal-light">
                      {formatLeadTime(lineAndHeadlineMean)}
                    </span>
                    <span className="text-slate-400 font-normal text-lg ml-2">
                      · {maDays}d window
                    </span>
                  </>
                ) : (
                  <>
                    {headlineLabel}:{' '}
                    <span className="text-brand-teal-light">
                      {formatLeadTime(lineAndHeadlineMean)}
                    </span>
                    <span className="text-slate-400 font-normal text-lg ml-2">
                      · {displayedCount} change{displayedCount !== 1 ? 's' : ''}
                    </span>
                  </>
                )}
              </>
            )}
          </p>
        </div>
        <div className={CHART_TOOLBAR_ROW_CLASS}>
          <div className={CHART_TOOLBAR_TOGGLE_GROUP_CLASS}>
            <button
              type="button"
              onClick={() => setUseMovingAverage(false)}
              disabled={loading}
              className={chartToolbarToggleBtnClass(!useMovingAverage)}
              aria-pressed={!useMovingAverage}
            >
              Raw
            </button>
            <button
              type="button"
              onClick={() => setUseMovingAverage(true)}
              disabled={loading}
              className={`${chartToolbarToggleBtnClass(useMovingAverage)} border-l border-dark-border`}
              aria-pressed={useMovingAverage}
            >
              MA
            </button>
          </div>
          <select
            aria-label="Moving average window"
            value={maWindow}
            onChange={(e) => setMaWindow(e.target.value)}
            disabled={loading || !useMovingAverage}
            className={CHART_TOOLBAR_SELECT_CLASS}
          >
            {MA_WINDOWS.map(({ id, label }) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
          <select
            aria-label="Chart type"
            value={chartType}
            onChange={(e) => onChartTypeChange(e.target.value)}
            disabled={loading}
            className={CHART_TOOLBAR_SELECT_CLASS}
          >
            {CHART_TYPES.map(({ id, label }) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
          <select
            aria-label="Period"
            value={period}
            onChange={(e) => onPeriodChange(e.target.value)}
            disabled={loading}
            className={CHART_TOOLBAR_SELECT_CLASS}
          >
            {PERIODS.map(({ id, label }) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {loading ? (
        <Skeleton className="w-full rounded-lg" style={{ height: `${height}px` }} aria-hidden />
      ) : (
        <Chart
          type={chartType === 'bar' ? 'bar' : 'area'}
          height={height}
          series={[{ name: 'Lead time (min)', data: seriesToRender }]}
          options={chartOptions}
        />
      )}
    </div>
  );
};

export default LeadTimeChart;
